import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentAccountOwnershipData,
  type EnrollmentAccountOwnershipSubmitInput,
  type EnrollmentAccountOwnershipSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment AccountOwnership data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentAccountOwnershipData>
 * @example const response = await getEnrollmentAccountOwnershipData()
 */
export const getEnrollmentAccountOwnershipData = async (
  context?: DefaultContext,
): Promise<EnrollmentAccountOwnershipData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentAccountOwnershipData',
      query: `query getEligibilityAccountData {
        enrollmentEligibility {
          canEnrollJointBrokerage
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { enrollmentEligibility: EnrollmentAccountOwnershipData }
  return response.enrollmentEligibility
}

/**
 * Submit Enrollment AccountOwnership data
 * @param data - Input data for the mutation
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentAccountOwnershipSubmitOutput>
 * @example const response = await submitEnrollmentAccountOwnershipData({ investmentProfile: { accountOwnership: { ownershipType: 'INDIVIDUAL' } } })
 */
export const submitEnrollmentAccountOwnershipData = async (
  data: EnrollmentAccountOwnershipSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentAccountOwnershipSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentAccountOwnershipData',
      query: `mutation submitEnrollmentAccountOwnershipData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: data,
    },
    context,
  )) as { submitEnrollment: EnrollmentAccountOwnershipSubmitOutput }
  return response.submitEnrollment
}

export const useGetEnrollmentAccountOwnershipData = createQueryHook<undefined, EnrollmentAccountOwnershipData>(
  getEnrollmentAccountOwnershipData,
)

export const useSubmitEnrollmentAccountOwnershipData = createMutationHook<
  EnrollmentAccountOwnershipSubmitInput,
  EnrollmentAccountOwnershipSubmitOutput
>(submitEnrollmentAccountOwnershipData)
